import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';

import { renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { createColorMappingInfo, getMappedColorLegendInfo } from '../../helpers/colorMappingInfo';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { currentTheme } from '../../../themes/themes';
import { CATEGORICAL_ORDERS, TpCategoryInfo, sortCategories } from '../../helpers/categoricalSorters';
import { getUserError } from '../../../../components/miscUtils';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

interface TpPieInfo extends TpCategoryInfo {
  sumForRadius: number;
  countForRadius: number;
  radius: number;
  angle: number;
  angleSelected: number;
  startAngle: number;
  index: number;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataCategories = boundData.bindings.value.data;
  const dataRadius = boundData.bindings.radius?.data;
  const dataItemId = boundData.bindings.itemId.data;
  const colInfoCategories = boundData.bindings.value.columnInfo;

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'value', 1, globalColorMappingConfig);

  let title = generateChartTitle(dataContext, dataBinding, config, boundData.bindings.value.columnInfo.name);
  if (dataRadius) title = `${title} vs. average ${boundData.bindings.radius?.columnInfo.name}`;
  const pieColName = boundData.bindings.value.columnInfo.name;

  const itemCount = dataCategories.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const pieMap: { [cat: string]: TpPieInfo } = {};
  const pies: TpPieInfo[] = [];
  for (let i = 0; i < itemCount; i++) {
    const catValue = dataCategories[i];
    if (!pieMap[catValue]) {
      const cat: TpPieInfo = {
        value: catValue,
        displayString: convertColumnDataToDisplayString(colInfoCategories, catValue),
        count: 0,
        sumForRadius: 0,
        countForRadius: 0,
        countSelected: 0,
        radius: 1,
        angle: 0,
        angleSelected: 0,
        startAngle: 0,
        index: 0,
      };
      pies.push(cat);
      pieMap[catValue] = cat;
    }
    pieMap[catValue].count++;
    // Skip NaN values for radius
    if (dataRadius && !isNaN(dataRadius[i])) {
      pieMap[catValue].sumForRadius += dataRadius[i];
      pieMap[catValue].countForRadius += 1;
    }
    if (isSelectedItem(selectedItemsMap, dataItemId[i])) pieMap[catValue].countSelected++;
  }

  // The accompanying color legend is always ordered by count, so pies should too.
  // Note: input should have already been sorted
  sortCategories(pies, CATEGORICAL_ORDERS.Count);

  let currentAngle = 0;
  let currentIndex = 0;
  for (const pie of pies) {
    pie.startAngle = currentAngle;
    pie.angle = (pie.count / itemCount) * 2 * Math.PI; // Fraction of full circle 2*Pi
    pie.angleSelected = (pie.countSelected / itemCount) * 2 * Math.PI;
    pie.index = currentIndex;
    currentAngle += pie.angle;
    currentIndex++;
    if (dataRadius) {
      // Calculate average value
      pie.radius = pie.sumForRadius / Math.max(pie.countForRadius, 1);
    }
  }

  const maxPieRadius = Math.max(...pies.map((cat) => cat.radius));

  // Cover situation where the radius calculation code could break
  if (maxPieRadius <= 0) {
    throw getUserError(`Radius data should contain positive values`);
  }

  function renderPies(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, mapsize, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    ctx.lineWidth = renderContext.pixelRatio * 1.0;

    const colorStringsUnselected = colorMappingInfo.colors.map((color) => color.opacity(0.6).toCanvasString());

    // Max radius on the size of the canvas, allowing for 5% margin on both ends
    const maxChartRadius = Math.min(mapx(0.45) - mapx(0), mapy(0) - mapy(0.45));
    for (const pie of pies) {
      // Fraction vs max value
      const radius = Math.max(0, (pie.radius / maxPieRadius) * maxChartRadius);
      const pieColorIndex = colorMappingInfo.colorCat2Index[pie.value];
      const canColor = pieColorIndex < colorMappingInfo.colors.length;

      ctx.fillStyle = 'rgb(210,210,210)';

      const centerAngle = pie.startAngle + pie.angle / 2;
      const offsX = (maxChartRadius / 200) * Math.sin(centerAngle);
      const offsY = (-maxChartRadius / 200) * Math.cos(centerAngle);
      const centerX = mapx(0.5) + offsX;
      const centerY = mapy(0.5) + offsY;

      if (canColor) ctx.fillStyle = colorStringsUnselected[pieColorIndex];
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius * 0.95, pie.startAngle - Math.PI / 2, pie.startAngle + pie.angle - Math.PI / 2);
      ctx.closePath();
      ctx.fill();

      if (canColor) ctx.fillStyle = colorMappingInfo.colorValueStrings[pieColorIndex];
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, pie.startAngle - Math.PI / 2, pie.startAngle + pie.angleSelected - Math.PI / 2);
      ctx.closePath();
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, pie.startAngle - Math.PI / 2, pie.startAngle + pie.angle - Math.PI / 2);
      ctx.closePath();
      ctx.strokeStyle = currentTheme.colors.background;
      ctx.stroke();
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  const hasLegend = true;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(0, 1);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'chart',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: false,
    zoomPanY: false,
    render: renderPies,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataCategories,
    colInfoCategories,
    itemCount,
    dataItemId,
    pies,
    pieMap,
    pieColName,
    title,
  };
}
